import { Component, OnInit } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { DashboardService, DueProtocol, RecentOrder, PaymentIssue } from 'src/app/core/services/dashboard.service';
import { ChartType } from './dashboard.model';
import { AuthService } from 'src/app/core/services/auth.service'; // ✅ import

export type ApexChartOptions = {
  series: any[];
  chart: { type: string; height: number; toolbar?: { show: boolean } };
  xaxis?: { categories: string[] };
  labels?: string[];
  legend?: { position?: string; show?: boolean };
  dataLabels?: { enabled: boolean };
  colors?: string[];
  stroke?: { curve?: string; width?: number[] | number; colors?: string[] };
  fill?: { type?: string; opacity?: number };
  plotOptions?: any;
  responsive?: any[];
  tooltip?: any;
};

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  selectedDate: NgbDateStruct | null = null;
  selectedDateLabel = 'Today';
  isStatsLoading = false;
  isDueProtocolsLoading = false;
  dueProtocols: DueProtocol[] = [];
  isOrderOverviewLoading = false;
  isRecentOrdersLoading = false;
  recentOrdersList: RecentOrder[] = [];
  isPaymentIssuesLoading = false;
  paymentIssuesList: PaymentIssue[] = [];

  // ✅ current logged-in user name
  loggedInUserName: string = '';

  orderOverview = {
    totalOrders: 0,
    pendingOrders: 0,
    processingOrders: 0,
    deliveredOrders: 0,
    onHoldOrders: 0
  };

  kpis = {
    totalPatients: 0,
    activeProtocols: 0,
    completedOrders: 0,
    ordersOnHold: 0
  };

  constructor(
    private dashboardService: DashboardService,
    private authService: AuthService // ✅ inject
  ) { }

  ngOnInit(): void {
    this.authService.refreshCurrentUser();
    this.loggedInUserName = this.authService.getUserName();

    // console.log('LOGGED IN USER NAME =>', this.loggedInUserName);

    this.loadDashboardStats();
    this.loadDueProtocols();
    this.loadOrderStatusOverview();
    this.loadRecentOrders();
    this.loadPaymentIssues();
  }

  // =========================
  // DASHBOARD API INTEGRATION
  // =========================

  loadDashboardStats(date?: string): void {
    this.isStatsLoading = true;

    this.dashboardService.getDashboardStats(date).subscribe({
      next: (res) => {
        const data = res?.data;

        this.kpis.totalPatients = Number(data?.totalPatients || 0);
        this.kpis.activeProtocols = Number(data?.activeProtocols || 0);
        this.kpis.completedOrders = Number(data?.totalCompletedOrders || 0);
        this.kpis.ordersOnHold = Number(data?.totalOnHoldOrders || 0);

        this.isStatsLoading = false;
      },
      error: (err) => {
        console.error('Dashboard stats fetch failed:', err);
        this.isStatsLoading = false;

        this.kpis.totalPatients = 0;
        this.kpis.activeProtocols = 0;
        this.kpis.completedOrders = 0;
        this.kpis.ordersOnHold = 0;
      }
    });
  }

  loadDueProtocols(): void {
    this.isDueProtocolsLoading = true;

    this.dashboardService.getDueProtocols({
      page: 1,
      limit: 10,
      sort: 'nextDueDate:asc'
    }).subscribe({
      next: (res) => {
        this.dueProtocols = res?.data?.protocols || [];
        this.isDueProtocolsLoading = false;
      },
      error: (err) => {
        console.error('Due protocols fetch failed:', err);
        this.dueProtocols = [];
        this.isDueProtocolsLoading = false;
      }
    });
  }

  onDateSelect(date: NgbDateStruct): void {
    this.selectedDate = date;

    const formattedDate = this.formatNgbDate(date);
    this.selectedDateLabel = formattedDate;

    this.loadDashboardStats(formattedDate);
    this.loadOrderStatusOverview(formattedDate);
    this.loadRecentOrders(formattedDate);
  }

  clearDateFilter(): void {
    this.selectedDate = null;
    this.selectedDateLabel = 'Today';
    this.loadDashboardStats();
    this.loadOrderStatusOverview();
    this.loadRecentOrders();
  }

  private formatNgbDate(date: NgbDateStruct | null): string {
    if (!date) return '';

    const year = date.year;
    const month = String(date.month).padStart(2, '0');
    const day = String(date.day).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  getInitials(name: string): string {
    if (!name) return 'NA';

    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }

  formatDisplayDate(date: string | null | undefined): string {
    if (!date) return '-';

    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';

    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  getDueText(date: string): string {
    if (!date) return '-';

    const dueDate = new Date(date);
    const today = new Date();

    const due = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffMs = due.getTime() - now.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays < 0) return `Overdue ${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? 's' : ''}`;
    return `In ${diffDays} day${diffDays > 1 ? 's' : ''}`;
  }

  getDueStatusClass(protocol: DueProtocol): string {
    return protocol?.dueState === 'OVERDUE' ? 'danger' : 'success';
  }

  getDueStatusLabel(protocol: DueProtocol): string {
    return protocol?.dueState === 'OVERDUE' ? 'Overdue' : 'Due Today';
  }

  getFulfillmentLabel(value: string): string {
    if (!value) return '-';
    return value.toLowerCase() === 'shipping' ? 'Delivery' : 'Pickup';
  }

  loadOrderStatusOverview(date?: string): void {
    this.isOrderOverviewLoading = true;

    this.dashboardService.getOrderStatusOverview(date).subscribe({
      next: (res) => {
        const data = res?.data;

        this.orderOverview.totalOrders = Number(data?.totalOrders || 0);
        this.orderOverview.pendingOrders = Number(data?.pendingOrders || 0);
        this.orderOverview.processingOrders = Number(data?.processingOrders || 0);
        this.orderOverview.deliveredOrders = Number(data?.deliveredOrders || 0);
        this.orderOverview.onHoldOrders = Number(data?.onHoldOrders || 0);

        this.statusChart = {
          ...this.statusChart,
          series: [
            this.orderOverview.pendingOrders,
            this.orderOverview.processingOrders,
            this.orderOverview.deliveredOrders,
            this.orderOverview.onHoldOrders
          ],
          labels: ['Pending', 'Processing', 'Delivered', 'On-Hold']
        };

        this.isOrderOverviewLoading = false;
      },
      error: (err) => {
        console.error('Order status overview fetch failed:', err);

        this.orderOverview.totalOrders = 0;
        this.orderOverview.pendingOrders = 0;
        this.orderOverview.processingOrders = 0;
        this.orderOverview.deliveredOrders = 0;
        this.orderOverview.onHoldOrders = 0;

        this.statusChart = {
          ...this.statusChart,
          series: [0, 0, 0, 0],
          labels: ['Pending', 'Processing', 'Delivered', 'On-Hold']
        };

        this.isOrderOverviewLoading = false;
      }
    });
  }

  loadRecentOrders(date?: string): void {
    this.isRecentOrdersLoading = true;

    this.dashboardService.getRecentOrders({
      date,
      limit: 5
    }).subscribe({
      next: (res) => {
        this.recentOrdersList = res?.data?.orders || [];
        this.isRecentOrdersLoading = false;
      },
      error: (err) => {
        console.error('Recent orders fetch failed:', err);
        this.recentOrdersList = [];
        this.isRecentOrdersLoading = false;
      }
    });
  }

  formatOrderTime(date: string | null | undefined): string {
    if (!date) return '-';

    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';

    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getRecentOrderStatusClass(status: string): string {
    switch (status) {
      case 'DELIVERED':
        return 'success';
      case 'PROCESSING':
        return 'info';
      case 'DISPATCHED':
        return 'warning';
      case 'ON_HOLD':
        return 'danger';
      case 'READY_FOR_DISPATCH':
        return 'secondary';
      case 'CANCELLED':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  getRecentOrderStatusLabel(status: string): string {
    switch (status) {
      case 'DELIVERED':
        return 'Delivered';
      case 'PROCESSING':
        return 'Processing';
      case 'DISPATCHED':
        return 'Dispatched';
      case 'ON_HOLD':
        return 'On-Hold';
      case 'READY_FOR_DISPATCH':
        return 'Pending';
      case 'CANCELLED':
        return 'Cancelled';
      default:
        return status || '-';
    }
  }

  getRecentOrderStatusIcon(status: string): string {
    switch (status) {
      case 'DELIVERED':
        return 'bi-check-circle';
      case 'PROCESSING':
        return 'bi-gear';
      case 'DISPATCHED':
        return 'bi-truck';
      case 'ON_HOLD':
        return 'bi-exclamation-triangle';
      case 'READY_FOR_DISPATCH':
        return 'bi-clock';
      case 'CANCELLED':
        return 'bi-x-circle';
      default:
        return 'bi-circle';
    }
  }

  formatCurrency(amount: number, currency: string = 'CAD'): string {
    try {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: currency || 'CAD',
        maximumFractionDigits: 0
      }).format(Number(amount || 0));
    } catch {
      return `${currency} ${amount || 0}`;
    }
  }

  getFulfillmentChipLabel(value: string): string {
    if (!value) return '-';
    return value === 'DELIVERY' ? 'Delivery' : 'Pickup';
  }

  getFulfillmentIcon(value: string): string {
    return value === 'DELIVERY' ? 'bi-truck' : 'bi-bag';
  }

  loadPaymentIssues(): void {
    this.isPaymentIssuesLoading = true;

    this.dashboardService.getPaymentIssues({
      limit: 5
    }).subscribe({
      next: (res) => {
        this.paymentIssuesList = res?.data?.issues || [];
        this.isPaymentIssuesLoading = false;
      },
      error: (err) => {
        console.error('Payment issues fetch failed:', err);
        this.paymentIssuesList = [];
        this.isPaymentIssuesLoading = false;
      }
    });
  }

  getPaymentIssueIconClass(issue: PaymentIssue): string {
    switch (issue.issueType) {
      case 'PAYMENT_FAILED':
        return 'bi-x-circle';
      case 'NO_PAYMENT_METHOD':
        return 'bi-credit-card';
      case 'INVOICE_OVERDUE':
        return 'bi-hourglass-split';
      case 'AUTOPAY_NO_DEFAULT_CARD':
        return 'bi-exclamation-triangle';
      case 'CARD_EXPIRING':
        return 'bi-credit-card-2-front';
      case 'ORDER_ON_HOLD_BILLING':
        return 'bi-pause-circle';
      case 'INACTIVE_BILLING_PROFILE':
        return 'bi-slash-circle';
      default:
        return 'bi-exclamation-circle';
    }
  }

  getPaymentIssueSeverityClass(issue: PaymentIssue): string {
    switch (issue.severity) {
      case 'HIGH':
        return 'danger';
      case 'MEDIUM':
        return 'warn';
      case 'LOW':
        return 'info';
      default:
        return 'warn';
    }
  }

  getPaymentIssueActionLabel(issue: PaymentIssue): string {
    return issue?.actionLabel || 'View';
  }

  formatIssueAmount(amount: number, currency: string = 'CAD'): string {
    if (!amount) return '-';

    try {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: currency || 'CAD',
        maximumFractionDigits: 0
      }).format(Number(amount || 0));
    } catch {
      return `${currency} ${amount || 0}`;
    }
  }

  quick = {
    todayOrders: 38,
    monthOrders: 840,
    totalRevenue: 1285000,
    totalCustomers: 1432,
    pendingOrders: 56,
    returnsCount: 12
  };

  salesChart: Partial<ApexChartOptions> = {
    series: [
      { name: 'Revenue', data: [12000, 18500, 14200, 22300, 19800, 25400, 31000] },
      { name: 'Orders', data: [120, 150, 130, 170, 160, 200, 230] }
    ],
    chart: { type: 'line', height: 320, toolbar: { show: false } },
    xaxis: { categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: [3, 3] },
    colors: ['#0d6efd', '#20c997'],
    legend: { position: 'top' },
    responsive: [
      { breakpoint: 576, options: { legend: { position: 'bottom' } } }
    ]
  };

  paymentChart: Partial<ApexChartOptions> = {
    series: [44100, 22500, 13800],
    labels: ['Cash On Delivery', 'Card', 'Wallet'],
    chart: { type: 'donut', height: 320, toolbar: { show: false } },
    legend: { position: 'bottom' },
    dataLabels: { enabled: true },
    colors: ['#0d6efd', '#198754', '#ffc107']
  };

  statusChart: ChartType = {
    series: [0, 0, 0, 0],
    labels: ['Pending', 'Processing', 'Delivered', 'On-Hold'],
    chart: { type: 'donut', height: 240, toolbar: { show: false } },

    plotOptions: {
      pie: {
        donut: {
          size: '72%',
          labels: {
            show: true,
            name: { show: true, offsetY: -6 },
            value: { show: true, offsetY: 8 },
            total: {
              show: true,
              label: 'Total Orders',
              formatter: (w: any) => {
                const sum = w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                return `${sum}`;
              }
            }
          }
        }
      }
    },

    dataLabels: { enabled: false },
    legend: { show: false },
    stroke: { width: 2, colors: ['#ffffff'] },
    colors: ['#c7cbd1', '#d1e2f9', '#b5ede1', '#f7d1d1'],
    tooltip: {
      y: { formatter: (val: number) => `${val} orders` }
    }
  };

  topCustomers = [
    { name: 'Ali Raza', email: 'ali@example.com', orders: 34, spend: 245000 },
    { name: 'Sara Khan', email: 'sara@example.com', orders: 29, spend: 198500 },
    { name: 'Usman Tariq', email: 'usman@example.com', orders: 26, spend: 176200 },
    { name: 'Hina Iqbal', email: 'hina@example.com', orders: 22, spend: 152900 },
    { name: 'Danish Ali', email: 'danish@example.com', orders: 20, spend: 139400 }
  ];

  inventoryValuation = {
    totalQty: 842,
    totalCost: 385000,
    totalRetail: 769500
  };

  lowStock = [
    { sku: 'SKU-101', name: 'Ocean Blue Perfume', qty: 3 },
    { sku: 'SKU-244', name: 'Matte Lipstick – Rose', qty: 5 },
    { sku: 'SKU-332', name: 'Hydrating Face Wash', qty: 7 },
    { sku: 'SKU-519', name: 'Almond Body Lotion', qty: 8 }
  ];

  returns = { count: 12, refundAmount: 58600 };

  recentOrders = [
    { id: 'INV-10121', customer: 'Ali Raza', amount: 12500, status: 'Processing', date: '2025-01-30' },
    { id: 'INV-10122', customer: 'Sara Khan', amount: 9800, status: 'Delivered', date: '2025-01-30' },
    { id: 'INV-10123', customer: 'Usman Tariq', amount: 13200, status: 'Pending', date: '2025-01-30' },
    { id: 'INV-10124', customer: 'Hina Iqbal', amount: 7600, status: 'Delivered', date: '2025-01-29' },
    { id: 'INV-10125', customer: 'Danish Ali', amount: 15400, status: 'Cancelled', date: '2025-01-29' }
  ];

  recentReturns = [
    { id: 'RET-9001', customer: 'Hina Iqbal', amount: 3200, reason: 'Damaged', date: '2025-01-29' },
    { id: 'RET-9002', customer: 'Ali Raza', amount: 1800, reason: 'Size Issue', date: '2025-01-28' },
    { id: 'RET-9003', customer: 'Sara Khan', amount: 2400, reason: 'Wrong Item', date: '2025-01-27' }
  ];
}